import {
  MeetingTranscript,
  MeetingTranscriptChunk,
  MeetingTranscriptSegment,
} from 'api/meeting/types';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVideoPlayerContext } from './player-context';
import {
  findSegmentAndChunkAtTime,
  scrollToTranscriptSegment,
} from './transcript-util';

type ActiveSegmentAndChunk = {
  segment?: MeetingTranscriptSegment;
  chunk?: MeetingTranscriptChunk;
};
type Props = {
  transcript: MeetingTranscript;
  isAutoScrollEnabled: boolean;
  beforeScrollToSegment: () => void;
};
export const useSyncTranscriptWithPlayer = ({
  transcript,
  isAutoScrollEnabled,
  beforeScrollToSegment,
}: Props) => {
  const { currentTime } = useVideoPlayerContext();
  const transcriptRef = useRef(transcript);
  const activeRef = useRef<ActiveSegmentAndChunk>({
    segment: undefined,
    chunk: undefined,
  });
  const [active, setActive] = useState<ActiveSegmentAndChunk>({
    segment: undefined,
    chunk: undefined,
  });
  const isAutoScrollEnabledRef = useRef(isAutoScrollEnabled);

  useEffect(() => {
    isAutoScrollEnabledRef.current = isAutoScrollEnabled;
  }, [isAutoScrollEnabled]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  const debouncedScroll = useMemo(
    () =>
      debounce(
        (time: number) => {
          const { segment, chunk } = findSegmentAndChunkAtTime(
            transcriptRef.current,
            time,
          );

          const currentActive = activeRef.current;
          const segmentChanged =
            segment?.start !== currentActive.segment?.start;
          const chunkChanged = chunk?.start !== currentActive.chunk?.start;

          if (segmentChanged) {
            if (!segment) {
              setActive({ segment: undefined, chunk: undefined });
              activeRef.current = { segment: undefined, chunk: undefined };
            } else {
              if (isAutoScrollEnabledRef.current) {
                beforeScrollToSegment();
                scrollToTranscriptSegment(segment);
              }
              setActive({ segment, chunk });
              activeRef.current = { segment, chunk };
            }
          } else if (chunkChanged) {
            setActive((prev) => ({ ...prev, chunk }));
            activeRef.current = { ...activeRef.current, chunk };
          }
        },
        300,
        { maxWait: 1000 },
      ),
    [beforeScrollToSegment],
  );

  useEffect(() => {
    debouncedScroll(currentTime);
  }, [currentTime, debouncedScroll]);

  useEffect(() => {
    return () => {
      debouncedScroll.cancel();
    };
  }, [debouncedScroll]);
  const scrollToSegment = useCallback(
    (segment: MeetingTranscriptSegment) => {
      beforeScrollToSegment();
      scrollToTranscriptSegment(segment);
    },
    [beforeScrollToSegment],
  );

  return {
    activeSegment: active.segment,
    activeChunk: active.chunk,
    scrollToSegment,
  };
};
